import { useHistory } from "react-router-dom";
import { RsvpActionButton } from "@circle-react/components/Events/NewEventsSpace/EventDetailPage/RsvpActionButton";
import { ListViewTicketInfo } from "@circle-react/components/Events/NewEventsSpace/ListViewItem/ListViewTicketInfo";
import {
  canRespondToInvitation,
  isEventFinished,
} from "@circle-react/components/Events/NewEventsSpace/shared/helpers";
import { STATUS } from "@circle-react/components/Events/helpers/constants";
import { isPaidEvent } from "@circle-react/components/EventsV3/Modal/utils";
import { eventsShowPath } from "@circle-react/helpers/urlHelpers";
import type { Event } from "@circle-react/types/Event";
import { Button } from "@circle-react-uikit/Button";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { Typography } from "@circle-react-uikit/Typography";
import { useEvent } from "../../hooks/useEvent";
import { EventTimestamp } from "../Events/EventTimeStamp";
import { Location } from "../Events/Location";

interface EventsItemProps {
  allEvents: Event[];
  eventItem: Event;
  onChange: (event: Event) => void;
}

export const EventItem = ({
  eventItem,
  allEvents,
  onChange,
}: EventsItemProps) => {
  const { eventData: eventDetail, isLoading } = useEvent({
    event: eventItem,
  });
  const history = useHistory();

  if (isLoading)
    return (
      <div className="border-primary bg-primary flex w-full flex-col border-b p-4 last:border-none md:h-auto md:flex-row">
        <SkeletonLoader variant="event-item" />
      </div>
    );

  const updatedEventData: Event =
    allEvents.find(item => item.id === eventItem.id) || eventItem;

  const event = {
    ...updatedEventData,
    ...eventDetail,
    rsvp_status: updatedEventData.rsvp_status,
  };
  const {
    event_setting_attributes: {
      hide_location_from_non_attendees: shouldHideLocationFromNonAttendees,
    },
    rsvped_event: isRsvpedEvent,
    status,
    cover_image_url: coverImageUrl,
    paywall_attributes: ticketData,
  } = event || { event_setting_attributes: {} };
  const isPublished = status === STATUS.PUBLISHED;

  const shouldHideLocationFromCurrentMember =
    shouldHideLocationFromNonAttendees && !isRsvpedEvent;

  const shouldDisplayRsvpAction =
    isPublished && !isEventFinished(event) && canRespondToInvitation(event);

  return (
    <div
      className="border-primary border-b py-9 first:pt-5 last:border-b-0 last:pb-5"
      id={String(event.id)}
      key={event.id}
    >
      {coverImageUrl && (
        <img
          loading="lazy"
          alt=""
          src={coverImageUrl}
          className="relative mb-4 h-48 w-full rounded-lg object-cover"
        />
      )}
      <div className="flex w-full items-start justify-between">
        <Button
          type="button"
          variant="plain"
          className="!p-0 !text-left"
          onClick={() =>
            history.push(
              eventsShowPath({
                spaceSlug: event.space_slug,
                eventSlug: event.slug,
              }),
            )
          }
        >
          <Typography.LabelLg weight="semibold">
            {event.display_title}
          </Typography.LabelLg>
        </Button>
        {shouldDisplayRsvpAction && (
          <div className="min-w-fit">
            <RsvpActionButton event={event} onChange={onChange} />
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center gap-4">
        <EventTimestamp eventData={event?.event_setting_attributes} />
        {!shouldHideLocationFromCurrentMember && <Location event={event} />}
        {isPaidEvent(event) && <ListViewTicketInfo ticketData={ticketData} />}
      </div>
    </div>
  );
};
